import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bot, MessageSquare, Users, CalendarCheck, BookOpen, GitBranch, Building2, TrendingUp, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const StatCard = ({ icon: Icon, label, value, subtext, color, testId }) => (
  <Card
    data-testid={testId}
    className="bg-white border-gray-200 hover:border-primary/30 transition-colors duration-300"
  >
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{label}</p>
          <p className="text-3xl font-['Manrope'] font-extrabold text-foreground">{value}</p>
          {subtext && <p className="text-xs text-muted-foreground/60 mt-1">{subtext}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center`} style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass-panel rounded-lg px-3 py-2 text-sm">
        <p className="text-muted-foreground">{label}</p>
        <p className="text-primary font-semibold">{payload[0].value} conversations</p>
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [billingInfo, setBillingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, bizRes] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/businesses"),
        ]);
        setStats(statsRes.data);
        setBusinesses(bizRes.data);
        if (user?.role !== "admin" && !(user?.email || "").endsWith("@leadbot.embed")) {
          try {
            const { data } = await api.get("/billing/status");
            if (data?.enforced) setBillingInfo(data);
          } catch { /* no billing */ }
        }
      } catch (e) {
        console.error("Failed to fetch data", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const s = stats || {};
  const chartData = (s.conversation_stats || []).map(d => ({
    date: d.date.slice(5),
    count: d.count,
  }));
  const activeBusinesses = businesses.filter(b => b.status === "active");

  return (
    <div data-testid="dashboard-page" className="p-8 animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-['Manrope'] font-extrabold text-foreground">{t("dash.title")}</h1>
          <span
            data-testid="dashboard-tagline"
            className="text-sm px-3 py-1 rounded-full bg-[#00E676]/10 text-[#00A858] border border-[#00E676]/30"
          >
            {t("dash.tagline")}
          </span>
        </div>
        <p className="text-muted-foreground mt-1">{t("dash.subtitle")}</p>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon={Building2} label={t("dash.businesses")} value={activeBusinesses.length} subtext={t("dash.activeLocations")} color="#BF5AF2" testId="stat-businesses" />
        <StatCard icon={Bot} label={t("dash.activeAgents")} value={s.active_agents || 0} color="#00E676" testId="stat-active-agents" />
        <StatCard icon={MessageSquare} label={t("dash.conversations")} value={s.total_conversations || 0} color="#0A84FF" testId="stat-conversations" />
        <StatCard icon={Users} label={t("dash.leads")} value={s.leads_qualified || 0} color="#FFD60A" testId="stat-leads" />
        <StatCard icon={CalendarCheck} label={t("dash.appointments")} value={s.appointments_booked || 0} color="#FF453A" testId="stat-appointments" />
      </div>

      {/* Plan & AI credits (direct customers with enforced billing) */}
      {billingInfo && (
        <Card className="bg-white border-primary/30 mb-6" data-testid="dashboard-plan-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-['Manrope'] font-semibold flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" /> {t("dash.planCard")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-bold">{billingInfo.billing?.plan === "agencia" ? "Small Agency" : "Personal"}</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${billingInfo.active ? "bg-green-100 text-green-700 border-green-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                {billingInfo.billing?.status === "trialing" ? t("dash.trialing") : billingInfo.active ? t("dash.active") : t("dash.suspended")}
              </span>
              {billingInfo.billing?.trial_ends && billingInfo.billing?.status === "trialing" && (
                <span className="text-xs text-muted-foreground">
                  {t("dash.trialEnds", { date: new Date(billingInfo.billing.trial_ends).toLocaleDateString() })}
                </span>
              )}
            </div>
            {billingInfo.ai_usage?.cap > 0 && (() => {
              const used = billingInfo.ai_usage.responses || 0;
              const cap = billingInfo.ai_usage.cap || 1;
              const pct = Math.min(100, Math.round((used / cap) * 100));
              const danger = used >= cap;
              return (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("dash.aiMonth")}</span>
                    <strong className={danger ? "text-destructive" : "text-foreground"}>{used} / {cap}</strong>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${danger ? "bg-destructive" : pct > 80 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                  </div>
                  {billingInfo.ai_usage.pack_balance > 0 && (
                    <p className="text-xs text-primary">
                      {t("dash.packBalance", { count: billingInfo.ai_usage.pack_balance })}
                    </p>
                  )}
                  {danger && (
                    <p className="text-xs text-destructive">{t("dash.limitReached")}</p>
                  )}
                </div>
              );
            })()}
            <div className="flex flex-wrap gap-2">
              {billingInfo.payment_links?.extra_number && (
                <Button variant="outline" size="sm" asChild>
                  <a href={billingInfo.payment_links.extra_number} target="_blank" rel="noopener noreferrer">{t("dash.buyNumber")}</a>
                </Button>
              )}
              {billingInfo.payment_links?.extra_subaccount && (
                <Button variant="outline" size="sm" asChild>
                  <a href={billingInfo.payment_links.extra_subaccount} target="_blank" rel="noopener noreferrer">{t("dash.buySub")}</a>
                </Button>
              )}
              {billingInfo.payment_links?.ai_credits && (
                <Button size="sm" asChild>
                  <a href={billingInfo.payment_links.ai_credits} target="_blank" rel="noopener noreferrer">{t("dash.buyCredits")}</a>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Businesses Quick View */}
      {activeBusinesses.length > 0 && (
        <Card className="bg-white border-gray-200 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-['Manrope'] font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#BF5AF2]" /> {t("dash.activeBusinesses")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {activeBusinesses.map(biz => (
                <div 
                  key={biz.id}
                  onClick={() => navigate("/businesses")}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary/30 hover:bg-secondary/50 cursor-pointer transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#BF5AF2]/10 flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-[#BF5AF2]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{biz.name}</p>
                    <p className="text-xs text-muted-foreground">{biz.ghl_location_id ? t("dash.crmConnected") : t("dash.noCrm")}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope'] font-semibold">Conversations (7 days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]" data-testid="conversations-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="date" stroke="#888" fontSize={12} />
                  <YAxis stroke="#888" fontSize={12} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#00E676" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope'] font-semibold">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(s.recent_conversations || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No recent conversations</p>
            ) : (
              (s.recent_conversations || []).map((c, i) => (
                <div
                  key={c.id || i}
                  className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 hover:bg-secondary cursor-pointer transition-colors duration-200"
                  onClick={() => navigate("/conversations")}
                  data-testid={`recent-convo-${i}`}
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{c.contact_name || "Unknown"}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {c.messages && c.messages.length > 0
                        ? c.messages[c.messages.length - 1].content
                        : "No messages"}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs border-border text-muted-foreground">
                    {c.channel || "web"}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <Card
          className="bg-white border-gray-200 hover:border-primary/30 cursor-pointer transition-colors duration-300"
          onClick={() => navigate("/agents")}
          data-testid="quick-link-agents"
        >
          <CardContent className="p-6 flex items-center gap-4">
            <Bot className="w-8 h-8 text-primary" />
            <div>
              <p className="font-semibold text-foreground">{s.total_agents || 0} Agents</p>
              <p className="text-sm text-muted-foreground">Manage your AI bots</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className="bg-white border-gray-200 hover:border-primary/30 cursor-pointer transition-colors duration-300"
          onClick={() => navigate("/knowledge-base")}
          data-testid="quick-link-kb"
        >
          <CardContent className="p-6 flex items-center gap-4">
            <BookOpen className="w-8 h-8 text-[#0A84FF]" />
            <div>
              <p className="font-semibold text-foreground">{s.total_kb_entries || 0} KB Entries</p>
              <p className="text-sm text-muted-foreground">Knowledge base articles</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className="bg-white border-gray-200 hover:border-primary/30 cursor-pointer transition-colors duration-300"
          onClick={() => navigate("/workflows")}
          data-testid="quick-link-workflows"
        >
          <CardContent className="p-6 flex items-center gap-4">
            <GitBranch className="w-8 h-8 text-[#FFD60A]" />
            <div>
              <p className="font-semibold text-foreground">{s.total_workflows || 0} Workflows</p>
              <p className="text-sm text-muted-foreground">Automation flows</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
